/**
 * The Archive modal's email editor and the content-rules explainer.
 *
 * Split out of FeedbackModalBodies so that file stays within the repo's 500-line limit;
 * these three pieces are self-contained and only ArchiveBody consumes them.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { cn } from '../../../../shared';
import { COLORS } from './format';
import {
  CONTENT_DESCRIPTION,
  CONTENT_LABEL,
  contentFor,
  DEPARTMENTS,
  STAGE_LABEL,
  STAGES,
  type Stage,
} from './emailContentRules';

/** The <a> the selection sits inside, if any (walking up to but not past the editor). */
function enclosingAnchor(editor: HTMLElement | null): HTMLAnchorElement | null {
  const sel = window.getSelection();
  if (!editor || !sel || sel.rangeCount === 0) return null;
  let node: Node | null = sel.getRangeAt(0).startContainer;
  while (node && node !== editor) {
    if (node instanceof HTMLAnchorElement) return node;
    node = node.parentNode;
  }
  return null;
}

/**
 * "Which email is this?" — the department × stage rules, with this candidate's cell
 * highlighted. Collapsed by default: it answers a question recruiters ask occasionally,
 * and should not compete with the email itself for attention.
 *
 * The rule lives in emailContentRules.ts, which a server-side test keeps in step with the
 * templates (see rejectionEmail.rules.test.js).
 */
export function EmailRulesInfo({ department, stage }: { department: string; stage: string }) {
  const [open, setOpen] = useState(false);
  const mine = contentFor(department, stage);
  const knownDept = DEPARTMENTS.some((d) => d.toLowerCase() === department.toLowerCase());
  const knownStage = (STAGES as readonly string[]).includes(stage);

  return (
    <div className="text-xs">
      <button
        type="button"
        className="text-blue-600 hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '▾' : '▸'} Which email does this candidate get?
      </button>

      {open && (
        <div className="mt-2 space-y-2 rounded-lg border p-3" style={{ borderColor: '#E3E3E3' }}>
          <p className="text-gray-700">
            <span className="font-semibold">This candidate:</span>{' '}
            {knownDept ? department : `${department || 'no department tag'} (not a known department)`} ·{' '}
            {knownStage ? STAGE_LABEL[stage as Stage] : `${stage || 'no stage'} (falls back to first interview)`} →{' '}
            <span className="font-semibold">{CONTENT_LABEL[mine]}</span>
          </p>
          <p className="text-gray-500">{CONTENT_DESCRIPTION[mine]}</p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="text-gray-500">
                  <th className="border-b py-1 pr-3 font-medium">Department</th>
                  {STAGES.map((st) => (
                    <th key={st} className="border-b py-1 pr-3 font-medium">
                      {STAGE_LABEL[st]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DEPARTMENTS.map((dep) => {
                  const isMyRow = dep.toLowerCase() === department.toLowerCase();
                  return (
                    <tr key={dep} className={cn(isMyRow && 'bg-blue-50')}>
                      <td className="border-b py-1 pr-3 font-medium text-gray-700">{dep}</td>
                      {STAGES.map((st) => {
                        const kind = contentFor(dep, st);
                        const isMyCell = isMyRow && st === stage;
                        return (
                          <td
                            key={st}
                            className={cn(
                              'border-b py-1 pr-3 text-gray-600',
                              isMyCell && 'font-semibold text-blue-700'
                            )}
                          >
                            {CONTENT_LABEL[kind]}
                            {isMyCell && ' ←'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-gray-500">
            Tech is the only department that gets the feedback written into the email, and only
            after a case or technical round. Everyone else is offered a call instead.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The email body editor: toolbar + the editable email in one component.
 *
 * ONE field that is both the editor and the rendered email — links are live inside the
 * text being edited, so there is no separate preview to drift out of sync. contentEditable
 * rather than a textarea, because a textarea cannot show a hyperlink.
 *
 * Clicking a linked word opens the URL row for that link automatically, prefilled; moving
 * the caret out of the link closes it again. The Link button is the path for creating a
 * new link from selected text.
 *
 * Two contentEditable details that are easy to get wrong and are load-bearing here:
 *   · toolbar buttons must preventDefault on mousedown, or focus leaves the editable and
 *     the selection collapses before execCommand can act on it;
 *   · the draft HTML is written to the DOM once per draft, never re-rendered from React
 *     state, or the caret jumps to the start of the field on every keystroke.
 *
 * execCommand is deprecated but universally implemented; the alternatives for a field this
 * small are hand-rolled Range surgery or a rich-text framework. styleWithCSS is forced off
 * so the browser emits <b>/<i> tags rather than styled spans — the sanitizer keeps tags
 * and drops spans, so CSS-based bold would silently vanish on save.
 *
 * On confirm the caller reads innerHTML; the server sanitises it to an allowlist.
 */
/**
 * What the "Help me write" bar needs from whoever owns the draft. Optional: a caller that passes no
 * `onRefine` gets no bar and no ✨ button, which is how the Archive modal stays out of scope
 * (Sofia's decision, Sep 1 2026) — by construction rather than by a flag someone can flip.
 */
export interface RefineProps {
  /** Runs the rewrite. Resolves TRUE only when new text landed in the editor — false on a refusal or failure. */
  onRefine: (instruction: string) => Promise<boolean>;
  busy: boolean;
  /** Specifics the rewrite asserts that nothing supports — advisory, shown amber. */
  warnings: string[];
  /** Why nothing was applied. The editor still holds the recruiter's own text. */
  refusal: string | null;
  /** Present once a rewrite has landed: puts the previous text back. */
  onUndo?: () => void;
}

export const EmailBodyEditor = forwardRef<
  HTMLDivElement,
  { html: string; disabled: boolean; onChanged: () => void; refine?: RefineProps }
>(function EmailBodyEditor({ html, disabled, onChanged, refine }, ref) {
  const inner = useRef<HTMLDivElement | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [editingExisting, setEditingExisting] = useState(false);
  const [url, setUrl] = useState('');
  const saved = useRef<Range | null>(null);
  const id = 'ta-email-body';

  useImperativeHandle(ref, () => inner.current as HTMLDivElement, []);

  // Write the draft in once per draft, not per render — see the note above.
  useEffect(() => {
    if (inner.current && inner.current.innerHTML !== html) inner.current.innerHTML = html;
  }, [html]);

  function selectAnchor(anchor: HTMLAnchorElement) {
    const range = document.createRange();
    range.selectNodeContents(anchor);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    saved.current = range.cloneRange();
  }

  /** Caret moved: show the link row when it lands in a link, hide it when it leaves. */
  function syncLinkRow() {
    if (disabled) return;
    const anchor = enclosingAnchor(inner.current);
    if (anchor) {
      setUrl(anchor.getAttribute('href') || '');
      setEditingExisting(true);
      setLinkOpen(true);
      saved.current = null; // re-resolved on Apply, so later edits cannot use a stale range
    } else if (editingExisting) {
      setLinkOpen(false);
      setEditingExisting(false);
    }
  }

  function run(command: string, value?: string) {
    inner.current?.focus();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false, value);
    onChanged();
  }

  function openLinkForSelection() {
    const anchor = enclosingAnchor(inner.current);
    if (anchor) {
      selectAnchor(anchor);
      setUrl(anchor.getAttribute('href') || '');
      setEditingExisting(true);
      setLinkOpen(true);
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      showToast({
        status: 'warning',
        description: 'Select some text to link, or click a link to edit it.',
      });
      return;
    }
    saved.current = sel.getRangeAt(0).cloneRange();
    setUrl('');
    setEditingExisting(false);
    setLinkOpen(true);
  }

  function applyLink() {
    const href = url.trim();
    if (!/^(https?:\/\/|mailto:)/i.test(href)) {
      showToast({
        status: 'warning',
        description: 'Enter a full URL starting with https:// or mailto:',
      });
      return;
    }
    const anchor = editingExisting ? enclosingAnchor(inner.current) : null;
    if (anchor) selectAnchor(anchor);
    else if (saved.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(saved.current);
    }
    run('createLink', href);
    setLinkOpen(false);
    setEditingExisting(false);
  }

  /**
   * Enter in the instruction field. Async and guarded on `busy` because Enter is easy to hit twice,
   * and a second rewrite firing on the first one's text would compound two edits the recruiter only
   * asked for once.
   */
  async function submitRefine() {
    const ask = instruction.trim();
    if (!ask || !refine || refine.busy) return;
    const applied = await refine.onRefine(ask);
    // Cleared on an APPLIED rewrite only. The first version cleared unconditionally — onRefine
    // resolves on refusals too — which threw away exactly the instruction worth rephrasing.
    if (applied) setInstruction('');
  }

  function removeLink() {
    const anchor = enclosingAnchor(inner.current);
    if (anchor) selectAnchor(anchor);
    run('unlink');
    setLinkOpen(false);
    setEditingExisting(false);
  }

  /**
   * ⌘/Ctrl+B and +I. The browser already handles these in contentEditable, but it may
   * apply them as <span style="font-weight:bold">, which the sanitizer drops — the
   * formatting would look right while typing and silently disappear on save. Routing them
   * through run() forces styleWithCSS off, so they come out as <b>/<i> tags that survive.
   *
   * ⌘U is swallowed deliberately: underline is not part of the email design and reads as
   * a broken link to a candidate, so it is better to do nothing than to lose it on save.
   */
  function onKeyDown(e: React.KeyboardEvent) {
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    const key = e.key.toLowerCase();
    if (key === 'b') {
      e.preventDefault();
      run('bold');
    } else if (key === 'i') {
      e.preventDefault();
      run('italic');
    } else if (key === 'u') {
      e.preventDefault();
    }
  }

  const noFocusSteal = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        <Button size="sm" variant="ghost" disabled={disabled} onMouseDown={noFocusSteal} onClick={() => run('bold')} title="Bold (⌘B)">
          <span className="font-bold">B</span>
        </Button>
        <Button size="sm" variant="ghost" disabled={disabled} onMouseDown={noFocusSteal} onClick={() => run('italic')} title="Italic (⌘I)">
          <span className="italic">I</span>
        </Button>
        <Button size="sm" variant="ghost" disabled={disabled} onMouseDown={noFocusSteal} onClick={openLinkForSelection} title="Link the selected text">
          Link
        </Button>
        <Button size="sm" variant="ghost" disabled={disabled} onMouseDown={noFocusSteal} onClick={removeLink} title="Remove the link under the cursor">
          ⛓️‍💥 Unlink
        </Button>
      </div>

      {linkOpen && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label={editingExisting ? 'Link URL (editing this link)' : 'Link URL'}
              value={url}
              autoFocus={!editingExisting}
              placeholder="https://…"
              onChange={(e) => setUrl((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                }
              }}
            />
          </div>
          <Button size="sm" onMouseDown={noFocusSteal} onClick={applyLink}>
            Apply
          </Button>
          <Button size="sm" variant="ghost" onMouseDown={noFocusSteal} onClick={() => setLinkOpen(false)}>
            Close
          </Button>
        </div>
      )}

      <div className="relative w-full">
        <div
          className={cn(
            'relative flex w-full flex-col bg-white pl-3 pt-4 text-black',
            'box-border border border-solid border-pearl',
            'focus-within:outline focus-within:outline-2 focus-within:outline-black',
            disabled && 'pointer-events-none cursor-default bg-snow fill-iron text-iron'
          )}
        >
          <div
            id={id}
            ref={inner}
            role="textbox"
            aria-multiline="true"
            aria-label="Email body"
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={onChanged}
            onClick={syncLinkRow}
            onKeyUp={syncLinkRow}
            onKeyDown={onKeyDown}
            className={cn(
              'flex min-h-[20rem] max-h-[26rem] w-full overflow-y-auto px-0 pb-2 pt-4',
              'box-border flex-col border-none outline-none',
              'body-16-regular',
              '[&_a]:text-blue-600 [&_a]:underline',
              disabled && 'bg-snow text-iron'
            )}
          />
          {/*
            The rewrite prompt lives INSIDE the email box, the way Gmail's does (Sofia, Sep 1 2026),
            and is ALWAYS THERE — not behind a toolbar toggle. It went through both: a panel above
            the toolbar read as a separate tool, and a ✨ button that revealed the field meant the
            feature was invisible until you knew to look for it ("its not there"). Gmail's own prompt
            simply sits in the compose area, so this one does too. It acts on the text directly
            above it, which is the other reason it belongs in the box rather than beside it.

            No autoFocus, deliberately: the field is present on every expanded card, so taking focus
            would put the caret in the prompt instead of the email the recruiter came to read.

            A design-system `Input` with its container restyled into a pill — `className` is merged
            through `cn`, so the height, border and background can be overridden without
            hand-rolling a field. No visible label on purpose: the placeholder and the ✨ say what it
            is, and a floating label inside a text box is noise. Hence `aria-label`.
          */}
          {refine && (
            <div className="mb-3 mr-3 mt-1 space-y-2" data-no-toggle>
              {/*
                A plain <input>, not the design-system `Input`, and this is the one place in this
                file that deviates from the design-system rule on purpose.

                `Input` is built for a labelled 56px form field and actively fights this use:
                `placeholder:text-opacity-0` hides the placeholder until focus, so the prompt
                rendered as a blank grey bar with no sign it was a field at all — which is exactly
                what "i cant click on the AI box nor write something" describes. It also wraps the
                control in two `relative w-full` divs to animate a floating label there is none of
                here. What is wanted is Gmail's borderless inline prompt, which is not a form field
                in a form; it is a caret in a strip inside the compose box. Reimplementing a
                labelled input would be wrong, and this is not that.
              */}
              <label
                className={cn(
                  'flex cursor-text items-center gap-2 rounded-full border border-solid px-3 py-2',
                  'border-gray-200 bg-gray-50 focus-within:border-blue-400 focus-within:bg-white'
                )}
              >
                <span aria-hidden="true" className="select-none text-base leading-none">
                  ✨
                </span>
                <input
                  type="text"
                  aria-label="What should change?"
                  className={cn(
                    'min-w-0 flex-1 border-none bg-transparent text-sm text-black outline-none',
                    'placeholder:text-gray-500'
                  )}
                  value={instruction}
                  disabled={disabled || refine.busy}
                  placeholder="Tell me what to change, then press Enter"
                  onChange={(e) => setInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter submits, Esc clears. Nothing to close: the prompt is part of the box.
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitRefine();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setInstruction('');
                    }
                  }}
                />
                {refine.busy ? (
                  <span className="whitespace-nowrap text-xs text-gray-500">Rewriting…</span>
                ) : (
                  <>
                    {refine.onUndo && (
                      <button
                        type="button"
                        className="whitespace-nowrap rounded-full px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
                        onMouseDown={noFocusSteal}
                        onClick={refine.onUndo}
                        title="Put the previous version back"
                      >
                        ↩ Undo
                      </button>
                    )}
                    {/* The keyboard is the intended path; this chip is the same action for a mouse. */}
                    <button
                      type="button"
                      className={cn(
                        'whitespace-nowrap rounded-full bg-gray-200 px-2 py-1 text-xs text-gray-700',
                        'disabled:opacity-40'
                      )}
                      disabled={!instruction.trim()}
                      onMouseDown={noFocusSteal}
                      onClick={() => void submitRefine()}
                    >
                      ⏎ Enter
                    </button>
                  </>
                )}
              </label>

              {/* A refusal means the editor was left alone: say so, or it reads as a lost edit. */}
              {refine.refusal && (
                <div className="rounded-lg bg-red-50 p-2 text-sm text-red-800">{refine.refusal}</div>
              )}

              {refine.warnings.map((warning) => (
                <div
                  key={warning}
                  className="rounded-lg p-2 text-sm"
                  style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
                >
                  <span className="font-semibold">Check this: </span>
                  {warning}
                </div>
              ))}
            </div>
          )}

          <label htmlFor={id} className={cn('absolute top-3 body-12-regular', disabled && 'text-iron')}>
            Email body
          </label>
        </div>
      </div>
    </div>
  );
});


